import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { h, defineComponent, ref, watch } from 'vue';
import { IdProps, toasted } from '@/ui/helpers';
import { useTask } from '@/ui/concerns';
import { get, intoSendable, update } from '@/services/api/resources/issue/public';
import { emptyIssue } from '@/ui/pages/issue/public/issueTypes';
import { IssueDetailData } from '@/entities/issue/public';
import { versioned } from '@/services/api';
import { t } from '@/ui/i18n/i18n';
import { v4 as uuidv4 } from 'uuid';
import { useIssueForm } from '@/ui/pages/issue/public/useIssueForm';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { learnMoreButton } from '@/ui/components/upgrade/learnMoreButton';
import { IssueStatus } from '@/entities/todo';

interface IssueExternalResolverPageProps extends IdProps {
  hash: string;
}

export const IssueExternalResolverPage = defineComponent<IssueExternalResolverPageProps>({
  name: 'IssueExternalResolverPage',
  props: ['hash', 'id'],
  setup(props) {
    // create empty record
    const recordEditable = ref<IssueDetailData>(emptyIssue());
    const disableUpdate = ref(false);

    let recordVersion = '';

    const loadIssue = useTask(
      () => get(props.hash, props.id),
      ({ data, version }) => {
        recordEditable.value = data.data;
        recordVersion = version;
      },
      uuidv4(),
      () => {
        toasted.error(t('common.error'));
      },
    );

    watch(ref(props.id), loadIssue.run);

    const sendRecord = useTask(
      () => {
        return update(props.hash, props.id, versioned(intoSendable(recordEditable.value), recordVersion));
      },
      () => {
        if ([IssueStatus.Done, IssueStatus.CantDo].includes(recordEditable.value.status)) {
          disableUpdate.value = true;
        } else {
          loadIssue.run();
        }
        toasted.success(t('issues.external-resolver.update-success'));
      },
      uuidv4(),
      () => {
        toasted.error(t('common.error'));
      },
    );

    const { formFields, handleSubmit } = useIssueForm(recordEditable, sendRecord, disableUpdate);

    return function render() {
      return container(
        [
          h(UpgradeAd, {
            class: 'upgrade-ad--external-resolver',
            props: {
              message: t('issues.external-resolver.ad'),
              isVertical: false,
            },
          }, [
            h('div', { slot: 'upgradeButton' }, [
              learnMoreButton({
                classes: 'upgrade-ad-message__button',
              }),
            ]),
          ]),
          pageTitle(t('issues.edit')),
          box([
            loader('form', loadIssue, [
              ...formFields(),
              loadingButton(
                t('issues.update'),
                handleSubmit,
                sendRecord.state.value.pending,
                { disabled: disableUpdate.value || sendRecord.state.value.pending },
              ),
            ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
